import { useHookstate } from "@hookstate/core";
import { MESSAGE_COUNT, SITEID_GLOBAL_STATE, UPDATED_CAR_DATA_STATE } from "globalStore/globalStore";
import moment from "moment";
import { useEffect } from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { getFeatureSetOption, getFeaturesList, isUserAdmin, isUserSupport } from "constant";
import { validateCarType } from "_helperFunctions/validateCarType";

const useInboxMessagesCount = () => {
  const { siteId } = useHookstate(SITEID_GLOBAL_STATE);
  const { MessageNumber, DismissTick } = useHookstate(MESSAGE_COUNT);
  const { updatedCarData } = useHookstate(UPDATED_CAR_DATA_STATE);
  const { getMessagesFromCarsData, getUserRole, siteSettingsData } =
    useSelector((state) => state.commonReducers) || [];
  const { carMessagesBackup } =
    useSelector((state) => state.firstReducer) || [];
  const { inboxMessagesCache } =
    useSelector((state) => state.secondReduces) || {};
  const [messageData, setMessageData] = useState([]);
  const enableMemberCar = isUserAdmin(getUserRole && getUserRole?.Role) || isUserSupport(getUserRole && getUserRole?.Role) ||
    !getFeatureSetOption(siteSettingsData?.Features, getFeaturesList().member_cars);
  useEffect(() => {
    if (!localStorage.getItem("dismissedIds")) {
      let tempData = [];
      localStorage.setItem("dismissedIds", JSON.stringify(tempData));
    }
  }, [localStorage.getItem("dismissedIds")]);

  // Switching sites must clear the badge immediately; the new site's messages
  // arrive on a later fetch. (#53026)
  useEffect(() => {
    if (!siteId.get()) return;

    setMessageData([]);
    MessageNumber.set(0);
  }, [siteId.get()]);
  function messageIsDiagnostic(msg) {
    return (
      msg?.Text?.indexOf("z1lk3r") >= 0 ||
      msg?.Text?.indexOf("v1s*g3") >= 0
    );
  }

  const isAdminOrSupport =
    isUserAdmin(getUserRole?.Role) ||
    isUserSupport(getUserRole?.Role);
  useEffect(() => {
    let dismissedIds = [];
    try {
      const dismissedIdsString = localStorage.getItem("dismissedIds");
      if (dismissedIdsString) {
        dismissedIds = JSON.parse(dismissedIdsString);
      }
    } catch (error) {
      console.error("Error parsing dismissedIds in useEffect:", error);
      localStorage.removeItem("dismissedIds"); // Clear corrupted data
    }

    // Only trust the payload if it belongs to the site currently selected,
    // otherwise fall back to that site's cache. (#53026)
    const sameSite = (dataSiteId) =>
      dataSiteId !== null &&
      dataSiteId !== undefined &&
      String(dataSiteId) === String(siteId.get());

    let cache = inboxMessagesCache;
    if (typeof cache === "string") {
      try {
        cache = JSON.parse(cache);
      } catch (e) {
        cache = null;
      }
    }

    const sourceMessages =
      sameSite(getMessagesFromCarsData?.SiteId) &&
      getMessagesFromCarsData?.Messages?.length
        ? getMessagesFromCarsData.Messages
        : sameSite(cache?.siteId)
        ? cache?.Messages
        : [];

    const notExpired = (item) =>
      !item?.Expires || !moment.utc().isAfter(moment.utc(item.Expires));

    let messageDataGetter = [];
    if (carMessagesBackup && carMessagesBackup?.length !== 0) {
      messageDataGetter =
        sourceMessages &&
        sourceMessages?.length !== 0 &&
        sourceMessages.filter(notExpired).filter((item) => {
          if (!carMessagesBackup.includes(item.Id)) {
            return {
              ...item,
              id: item?.Id,
              received: moment(item?.Created).unix(),
              car: item?.CarNum,
              text: item?.Text,
              response: item?.Response,
              hole: item?.Hole,
            };
          }
        });
    } else {
      messageDataGetter =
        sourceMessages && sourceMessages?.length !== 0
          ? sourceMessages.filter(notExpired).map((item) => {
            return {
              ...item,
              id: item?.Id,
              received: moment(item?.Created).unix(),
              car: item?.CarNum,
              text: item?.Text,
              response: item?.Response,
              hole: item?.Hole,
            };
          })
          : [];
    }

    const finalMessages = (messageDataGetter || []).filter((msg) => {
      if (messageIsDiagnostic(msg) && !isAdminOrSupport) {
        return false;
      }
      if (!isAdminOrSupport && (msg?.Text || "").indexOf("Download SW") >= 0) {
        return false;
      }
      if (dismissedIds.includes(msg.Id)) {
        return false;
      }
      if (!enableMemberCar && !validateCarType(msg.CarNum, updatedCarData.get())) {
        return false;
      }
      // Hiding messages from negative car numbers for non-admin/support users
      if (!isAdminOrSupport && Number(msg.CarNum) < 0) {
        return false;
      }
      return true;
    });

    // Always write, including 0 — otherwise a site with no messages keeps the
    // previous site's count on the badge. (#53026)
    setMessageData(finalMessages);
    MessageNumber.set(finalMessages.length);
  }, [

    carMessagesBackup,
    getMessagesFromCarsData,
    inboxMessagesCache,
    siteId.get(),
    enableMemberCar,
    isAdminOrSupport,
    DismissTick.get()

  ]);

  return {
    msgInboxCount: messageData.length,
  };
};

export default useInboxMessagesCount;
